import { Fragment, useEffect, useState } from 'react'
import { Screen } from '@/components/layout'
import { ConfirmDialog } from '@/components/dialog'
import { Button, InlineError } from '@/components/primitives'
import { AccountRow, InstitutionRow, List } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList } from '@/components/states'
import { api, asApiFailure } from '@/lib/api'
import type { BankConnection } from '@/generated/types'
import { t, tServer } from '@/lib/copy'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/** api.json: "Poll every 2s while status is syncing." */
const POLL_MS = 2000

/**
 * Manage existing connections. reauthRequired is a recoverable state, not a
 * dead link — it offers Reconnect, which reopens Link in update mode via
 * linkAccount rather than making the user delete and start over.
 */
export function ConnectedAccountsScreen() {
  const navigate = useNavigate()
  const [tick, setTick] = useState(0)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<BankConnection | null>(null)

  const connections = useAsync((signal) => api.plaid.items(signal), [tick])
  const items = connections.state.data ?? []

  // Best-effort, like linkSuccess: a failed account fetch leaves the
  // connection cards intact rather than failing the screen.
  const accounts = useAsync((signal) => api.accounts.list(signal), [tick])
  const accountList = accounts.state.data ?? []

  const syncing = items.some((item) => item.status === 'syncing')
  const reauthRequired = items.some((item) => item.status === 'reauthRequired')

  useEffect(() => {
    if (!syncing) return
    const timer = setTimeout(() => setTick((n) => n + 1), POLL_MS)
    return () => clearTimeout(timer)
  }, [syncing, tick])

  async function sync(id: string) {
    setBusyId(id)
    setActionError(null)
    try {
      await api.plaid.sync(id)
      setTick((n) => n + 1)
    } catch (e) {
      // A sync that will not start is a row-level problem, not a screen-level
      // one — the other connections are still fine, so the list stays up.
      setActionError(asApiFailure(e).copyKey)
    } finally {
      setBusyId(null)
    }
  }

  async function disconnect(connection: BankConnection) {
    setBusyId(connection.id)
    setActionError(null)
    try {
      await api.plaid.remove(connection.id)
      setConfirming(null)
      setTick((n) => n + 1)
    } catch (e) {
      setActionError(asApiFailure(e).copyKey)
    } finally {
      setBusyId(null)
    }
  }

  const state =
    connections.state.status === 'error'
      ? 'error'
      : connections.state.status === 'loading' && !connections.state.data
        ? 'loading'
        : items.length === 0
          ? 'empty'
          : reauthRequired
            ? 'reauthRequired'
            : syncing
              ? 'syncing'
              : 'ready'

  return (
    <Screen
      id="connectedAccounts"
      state={state}
      back={{ screen: 'settings' }}
      actions={
        state === 'loading' || state === 'empty' ? undefined : (
          <Button variant="primary" onClick={() => navigate('linkAccount')}>
            {t('connectedAccounts.cta.add')}
          </Button>
        )
      }
    >
      {state === 'loading' ? (
        <LoadingRegion>
          <SkeletonList rows={3} />
        </LoadingRegion>
      ) : state === 'error' ? (
        <ErrorState
          titleKey="connectedAccounts.error.title"
          error={connections.state.status === 'error' ? connections.state.error : undefined}
          onRetry={connections.reload}
        />
      ) : state === 'empty' ? (
        <EmptyState
          titleKey="connectedAccounts.empty.title"
          actionLabel={t('connectedAccounts.empty.cta')}
          onAction={() => navigate('linkAccount')}
        />
      ) : (
        <div className="stack">
          {reauthRequired ? <InlineError>{t('connectedAccounts.error.reauthRequired')}</InlineError> : null}
          {/* The Server's own copyKey when it names something renderable, so a
              failed Disconnect does not report itself as a failed sync. */}
          {actionError ? (
            <InlineError>{tServer(actionError, undefined, 'connectedAccounts.error.syncFailed')}</InlineError>
          ) : null}

          <List>
            {items.map((connection) => (
              <Fragment key={connection.id}>
              <InstitutionRow
                connection={connection}
                actions={
                  <>
                    {connection.status === 'reauthRequired' ? (
                      <Button
                        variant="primary"
                        onClick={() => navigate('linkAccount', {}, { query: { relink: connection.id } })}
                      >
                        {t('connectedAccounts.cta.reconnect')}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        disabled={busyId === connection.id || connection.status === 'syncing'}
                        onClick={() => sync(connection.id)}
                      >
                        {t('connectedAccounts.cta.sync')}
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      disabled={busyId === connection.id}
                      onClick={() => setConfirming(connection)}
                    >
                      {t('connectedAccounts.cta.disconnect')}
                    </Button>
                  </>
                }
              />
              {/* screens.json: connectedAccounts navigatesTo accountDetail —
                  each account this connection feeds is a row into its detail. */}
              {accountList
                .filter((account) => connection.accountIds.includes(account.id))
                .map((account) => (
                  <AccountRow
                    key={account.id}
                    account={account}
                    onSelect={() => navigate('accountDetail', { id: account.id })}
                  />
                ))}
              </Fragment>
            ))}
          </List>
        </div>
      )}

      {confirming ? (
        <ConfirmDialog
          title={t('connectedAccounts.disconnect.confirm.title', { name: confirming.institutionName })}
          body={t('connectedAccounts.disconnect.confirm.body')}
          confirmLabel={t('connectedAccounts.disconnect.confirm.cta')}
          busy={busyId === confirming.id}
          onConfirm={() => disconnect(confirming)}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </Screen>
  )
}
